/**
 * Dashboard Quick Links — Spinnable wheel variant.
 *
 * UX: 8 icons sit on the perimeter of a circle. Drag/swipe left or right to
 * rotate the wheel. The icon currently at the top (12 o'clock) is the
 * "focused" one — its label shows in the centre, and tapping it (or pressing
 * GO) navigates. Drag snaps to the nearest item on release.
 *
 * Visual language: blue (sky-400) accents on dark, vs. the rest of the app's
 * gold — keeps this section feeling like a dedicated control surface.
 */
import { useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useMotionValue, animate, AnimatePresence } from 'framer-motion';
import { Users, Target, Trophy, Timer, Dumbbell, Crown, Flame, Apple, Zap, ChevronUp } from 'lucide-react';

const defaultLinks = [
  { to: '/social',      Icon: Users,    label: 'Social',      color: 'text-sky-300',     glow: 'rgba(56,189,248,0.7)',  emoji: '👥' },
  { to: '/workouts',    Icon: Dumbbell, label: 'Workouts',    color: 'text-primary',     glow: 'rgba(250,204,21,0.75)', emoji: '🏋️' },
  { to: '/exercises',   Icon: Flame,    label: 'Exercises',   color: 'text-orange-400',  glow: 'rgba(251,146,60,0.8)',  emoji: '🔥' },
  { to: '/food-log',    Icon: Apple,    label: 'Food Log',    color: 'text-red-400',     glow: 'rgba(248,113,113,0.8)', emoji: '🍎' },
  { to: '/challenges',  Icon: Target,   label: 'Challenges',  color: 'text-rose-400',    glow: 'rgba(251,113,133,0.75)',emoji: '🎯' },
  { to: '/leaderboard', Icon: Trophy,   label: 'Ranks',       color: 'text-amber-300',   glow: 'rgba(252,211,77,0.8)',  emoji: '🏆' },
  { to: '/timer',       Icon: Timer,    label: 'Timer',       color: 'text-cyan-300',    glow: 'rgba(103,232,249,0.75)',emoji: '⏱️' },
  { to: '/badges',      Icon: Crown,    label: 'Trophy Case', color: 'text-yellow-300',  glow: 'rgba(253,224,71,0.8)',  emoji: '👑' },
];

const RADIUS = 90;
const WHEEL_SIZE = 220;

/**
 * Convert a wheel rotation in degrees → index of the item currently at the
 * top (12 o'clock).
 */
const indexAtTop = (rotationDeg, itemCount) => {
  const step = 360 / itemCount;
  const normalized = ((-rotationDeg % 360) + 360) % 360;
  return Math.round(normalized / step) % itemCount;
};

export const QuickLinksWheel = ({
  links = defaultLinks,
  storageKey = 'aas:wheel:lastIdx',
  triggerLabel = 'Ascend to',
}) => {
  const items = links;
  const step = 360 / items.length;
  const navigate = useNavigate();

  // Restore the wheel to wherever the user last navigated from it.
  const initialIdx = (() => {
    try {
      const raw = localStorage.getItem(storageKey);
      const n = raw == null ? 0 : parseInt(raw, 10);
      return Number.isFinite(n) && n >= 0 && n < items.length ? n : 0;
    } catch {
      return 0;
    }
  })();

  const rotation = useMotionValue(-initialIdx * step);
  const [activeIdx, setActiveIdx] = useState(initialIdx);
  const [expanded, setExpanded] = useState(false);
  const dragOriginRef = useRef({ x: 0, rotation: 0 });
  const wheelRef = useRef(null);

  const snap = useCallback((finalRotation) => {
    const idx = indexAtTop(finalRotation, items.length);
    const targetRotation = -idx * step;
    // Pick the closest equivalent angle so we don't spin the long way round
    const current = rotation.get();
    const delta = ((targetRotation - current + 540) % 360) - 180;
    animate(rotation, current + delta, {
      type: 'spring', stiffness: 220, damping: 26,
      onUpdate: (v) => setActiveIdx(indexAtTop(v, items.length)),
    });
  }, [rotation, items.length, step]);

  // Horizontal drag rotates the wheel. dx (px) → degrees with a sensitivity
  // factor so small swipes still produce a satisfying rotation.
  // Capture the pointer only once a real drag starts so plain clicks reach the icons (desktop).
  const justDraggedRef = useRef(false);
  const onPointerDown = (e) => {
    dragOriginRef.current = { x: e.clientX, rotation: rotation.get(), moved: false, id: e.pointerId };
  };
  const onPointerMove = (e) => {
    if (!e.buttons && e.pointerType === 'mouse') return;
    const o = dragOriginRef.current;
    if (o.x == null) return;
    const dx = e.clientX - o.x;
    if (!o.moved) {
      if (Math.abs(dx) < 6) return;
      o.moved = true;
      e.currentTarget.setPointerCapture?.(o.id);
    }
    const next = o.rotation + dx * 0.6;
    rotation.set(next);
    setActiveIdx(indexAtTop(next, items.length));
  };
  const onPointerUp = () => {
    const o = dragOriginRef.current;
    if (o.moved) {
      justDraggedRef.current = true;
      setTimeout(() => { justDraggedRef.current = false; }, 0);
      snap(rotation.get());
    }
    dragOriginRef.current = { x: null, rotation: rotation.get(), moved: false };
  };

  const spinTo = (i) => {
    const current = rotation.get();
    const target = -i * step;
    const delta = ((target - current + 540) % 360) - 180;
    animate(rotation, current + delta, {
      type: 'spring', stiffness: 220, damping: 26,
      onUpdate: (v) => setActiveIdx(indexAtTop(v, items.length)),
    });
  };

  const active = items[activeIdx];
  const Active = active.Icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="mb-8 flex flex-col items-center select-none w-full"
      data-testid="dashboard-quick-links-wheel"
    >
      {/* Collapsed trigger — a slim pill that nudges users to discover the
          wheel. Tapping expands the wheel below it. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid="quick-links-wheel-toggle"
        className="group w-full flex items-center justify-center gap-3 px-5 py-3 rounded-full border border-sky-400/40 bg-sky-400/10 hover:bg-sky-400/20 text-white transition-colors"
        style={{
          textShadow:
            '0 0 4px rgba(255,255,255,0.95), 0 0 10px rgba(255,255,255,0.7), 0 0 18px rgba(56,189,248,0.7)',
        }}
      >
        <Zap className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        <span className="font-heading text-xs uppercase tracking-[0.2em]">
          {expanded ? 'Close menu' : triggerLabel}
        </span>
        <ChevronUp className={`h-3.5 w-3.5 transition-transform ${expanded ? '' : 'rotate-180'}`} />
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center overflow-hidden w-full"
          >
            <div className="mt-3" />
            {/* Full-circle wheel — every icon is visible. A small downward
                pointer above 12 o'clock shows which icon is currently
                "selected" (i.e., will be opened by tapping GO). */}
            <div className="relative" style={{ width: WHEEL_SIZE, height: WHEEL_SIZE }}>
              {/* Selection indicator pointing down at the top icon */}
              <div className="absolute left-1/2 -translate-x-1/2 -top-2 z-10 pointer-events-none">
                <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[10px] border-t-sky-400 drop-shadow-[0_0_8px_rgba(56,189,248,0.7)]" />
              </div>
              <motion.div
                ref={wheelRef}
                style={{
                  width: WHEEL_SIZE,
                  height: WHEEL_SIZE,
                  rotate: rotation,
                }}
                className="absolute left-0 top-0 rounded-full border border-sky-400/30 bg-gradient-to-b from-sky-500/10 to-transparent touch-none cursor-grab active:cursor-grabbing"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                data-testid="quick-links-wheel-disc"
              >
                {/* Decorative inner ornaments — spokes, dashed rings, ticks.
                    These rotate WITH the wheel so they reinforce the spin
                    motion. SVG keeps it crisp at any size. */}
                <svg
                  className="absolute inset-0 pointer-events-none"
                  viewBox={`0 0 ${WHEEL_SIZE} ${WHEEL_SIZE}`}
                  aria-hidden="true"
                >
                  <defs>
                    <radialGradient id="wheelGlow" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="rgba(56,189,248,0.35)" />
                      <stop offset="60%" stopColor="rgba(56,189,248,0.05)" />
                      <stop offset="100%" stopColor="rgba(56,189,248,0)" />
                    </radialGradient>
                  </defs>

                  {/* Soft inner glow disc */}
                  <circle cx={WHEEL_SIZE / 2} cy={WHEEL_SIZE / 2} r={RADIUS - 12} fill="url(#wheelGlow)" />

                  {/* Outer dashed ring just inside the icon track */}
                  <circle
                    cx={WHEEL_SIZE / 2}
                    cy={WHEEL_SIZE / 2}
                    r={RADIUS - 6}
                    fill="none"
                    stroke="rgba(56,189,248,0.28)"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />

                  {/* Radial spokes — one per item, faint lines from center
                      out toward each icon. Gives the wheel a "compass" feel. */}
                  {items.map((_, i) => {
                    const a = (i * step - 90) * (Math.PI / 180);
                    const x1 = WHEEL_SIZE / 2 + Math.cos(a) * (RADIUS - 48);
                    const y1 = WHEEL_SIZE / 2 + Math.sin(a) * (RADIUS - 48);
                    const x2 = WHEEL_SIZE / 2 + Math.cos(a) * (RADIUS - 10);
                    const y2 = WHEEL_SIZE / 2 + Math.sin(a) * (RADIUS - 10);
                    return (
                      <line
                        key={`spoke-${i}`}
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke="rgba(56,189,248,0.25)"
                        strokeWidth="1"
                      />
                    );
                  })}

                  {/* 48 dense tick marks around the outer rim — major every 6 */}
                  {Array.from({ length: 48 }).map((_, i) => {
                    const a = (i * 7.5 - 90) * (Math.PI / 180);
                    const isMajor = i % 6 === 0;
                    const isMid = !isMajor && i % 3 === 0;
                    const inner = isMajor ? RADIUS - 4 : RADIUS - 2;
                    const outer = isMajor ? RADIUS + 3 : RADIUS + 1.5;
                    const x1 = WHEEL_SIZE / 2 + Math.cos(a) * inner;
                    const y1 = WHEEL_SIZE / 2 + Math.sin(a) * inner;
                    const x2 = WHEEL_SIZE / 2 + Math.cos(a) * outer;
                    const y2 = WHEEL_SIZE / 2 + Math.sin(a) * outer;
                    return (
                      <line
                        key={`tick-${i}`}
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={
                          isMajor ? 'rgba(56,189,248,0.75)'
                          : isMid ? 'rgba(56,189,248,0.4)'
                          : 'rgba(56,189,248,0.22)'
                        }
                        strokeWidth={isMajor ? 1.5 : 0.8}
                      />
                    );
                  })}

                  {/* Inner stud dots — 16 small filled circles between mid
                      and inner ring for that "instrument panel" texture. */}
                  {Array.from({ length: 16 }).map((_, i) => {
                    const a = (i * 22.5 - 90) * (Math.PI / 180);
                    const r = RADIUS - 38;
                    const cx = WHEEL_SIZE / 2 + Math.cos(a) * r;
                    const cy = WHEEL_SIZE / 2 + Math.sin(a) * r;
                    return (
                      <circle
                        key={`stud-${i}`}
                        cx={cx} cy={cy} r={1.4}
                        fill="rgba(56,189,248,0.55)"
                        style={{
                          animation: 'wheel-stud-twinkle 3s ease-in-out infinite',
                          animationDelay: `${(i * 0.18).toFixed(2)}s`,
                        }}
                      />
                    );
                  })}

                  {/* Counter-rotating starburst — extra fine spokes between
                      the icon spokes for a busier, mechanical core. */}
                  <g className="radar-sweep-reverse" style={{ transformOrigin: `${WHEEL_SIZE / 2}px ${WHEEL_SIZE / 2}px` }}>
                    {Array.from({ length: 16 }).map((_, i) => {
                      const a = (i * 22.5 + 11.25 - 90) * (Math.PI / 180);
                      const x1 = WHEEL_SIZE / 2 + Math.cos(a) * (RADIUS - 48);
                      const y1 = WHEEL_SIZE / 2 + Math.sin(a) * (RADIUS - 48);
                      const x2 = WHEEL_SIZE / 2 + Math.cos(a) * (RADIUS - 30);
                      const y2 = WHEEL_SIZE / 2 + Math.sin(a) * (RADIUS - 30);
                      return (
                        <line
                          key={`mini-spoke-${i}`}
                          x1={x1} y1={y1} x2={x2} y2={y2}
                          stroke="rgba(56,189,248,0.22)"
                          strokeWidth="0.8"
                        />
                      );
                    })}
                  </g>
                </svg>

                {/* Radar sweep — continuously rotates regardless of wheel
                    drag, giving the wheel a "live scanning" feel. */}
                <div
                  className="radar-sweep absolute inset-0 rounded-full pointer-events-none mix-blend-screen"
                  aria-hidden="true"
                  style={{
                    background:
                      'conic-gradient(from 0deg, rgba(56,189,248,0) 0deg, rgba(56,189,248,0) 320deg, rgba(56,189,248,0.55) 358deg, rgba(56,189,248,0.85) 360deg, rgba(56,189,248,0) 360deg)',
                    WebkitMaskImage:
                      'radial-gradient(circle, black 35%, black 78%, transparent 80%)',
                    maskImage:
                      'radial-gradient(circle, black 35%, black 78%, transparent 80%)',
                  }}
                />
                {items.map(({ Icon, label, color, glow, emoji }, i) => {
                  const angle = (i * step - 90) * (Math.PI / 180); // start at top
                  const x = WHEEL_SIZE / 2 + Math.cos(angle) * RADIUS - 22;
                  const y = WHEEL_SIZE / 2 + Math.sin(angle) * RADIUS - 22;
                  const isActive = i === activeIdx;
                  return (
                    <motion.button
                      key={label}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (justDraggedRef.current) return;
                        // Any icon click navigates directly — no need to
                        // spin first and hunt for the GO pill. Big win on
                        // desktop where users expect to click any icon.
                        try { localStorage.setItem(storageKey, String(i)); } catch { /* ignore */ }
                        if (!isActive) spinTo(i);
                        navigate(items[i].to);
                      }}
                      style={{
                        position: 'absolute',
                        left: x,
                        top: y,
                        width: 44,
                        height: 44,
                        rotate: rotation.get() * -1,
                      }}
                      animate={
                        isActive
                          ? { scale: [1.1, 1.25, 1.1] }
                          : { scale: 0.92 }
                      }
                      transition={
                        isActive
                          ? { duration: 1.8, repeat: Infinity, ease: 'easeInOut' }
                          : { type: 'spring', stiffness: 300, damping: 22 }
                      }
                      className={`rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                        isActive
                          ? 'bg-black/70 border-2 border-sky-300 shadow-[0_0_20px_rgba(56,189,248,0.7)]'
                          : `bg-gradient-to-br from-card to-black/50 border border-sky-400/30 ${color} hover:border-white/60 hover:brightness-125`
                      }`}
                      data-testid={`quick-link-wheel-${label.toLowerCase().replace(/\s/g, '-')}`}
                      aria-label={label}
                    >
                      {isActive ? (
                        <span
                          className="text-2xl leading-none"
                          style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.5))' }}
                          aria-hidden="true"
                        >
                          {emoji}
                        </span>
                      ) : (
                        <Icon
                          className="h-5 w-5"
                          strokeWidth={2.5}
                          style={{ filter: `drop-shadow(0 0 6px ${glow})` }}
                        />
                      )}
                    </motion.button>
                  );
                })}
              </motion.div>

              {/* Centre badge — sits inside the wheel showing the active item
                  and a tappable GO link. Not part of the rotating motion.div
                  so its transform isn't affected by the spin. */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5 pointer-events-none">
                <p
                  className="font-heading text-[10px] text-white uppercase tracking-[0.2em] text-center max-w-[110px]"
                  style={{
                    textShadow:
                      '0 0 4px rgba(255,255,255,0.95), 0 0 10px rgba(255,255,255,0.7), 0 0 18px rgba(56,189,248,0.7)',
                  }}
                  data-testid="quick-links-wheel-active-label"
                >
                  {active.label}
                </p>
                <Link
                  to={active.to}
                  onClick={() => {
                    try { localStorage.setItem(storageKey, String(activeIdx)); } catch { /* ignore quota errors */ }
                  }}
                  className="pointer-events-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sky-400 text-black font-heading text-[9px] uppercase tracking-wider hover:bg-sky-300 transition-colors shadow-lg shadow-sky-400/30"
                  data-testid="quick-links-wheel-go"
                >
                  <span className="text-sm leading-none" aria-hidden="true">{active.emoji}</span> Open
                </Link>
              </div>
            </div>

            <p
              className="mt-3 text-[10px] text-white uppercase tracking-wider pb-2 font-medium"
              style={{
                textShadow:
                  '0 0 4px rgba(255,255,255,0.95), 0 0 10px rgba(255,255,255,0.7), 0 0 18px rgba(56,189,248,0.6)',
              }}
            >
              <span className="md:hidden">Swipe the wheel to spin</span>
              <span className="hidden md:inline">Click an icon or drag to spin</span>
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default QuickLinksWheel;
